import { createSelector } from 'reselect';

import { dataDaiRowsFilteredByMultiFilter } from '../../domain/dai/dataDaiFilteredByMultiFilter';
import { dataDaiDefaultValue } from '../../domain/dai/defaultValue';
import { sortDai } from '../../domain/dai/sortDai';
import { DataDai } from '../../domain/dai/types';
import { ClarisApiError } from '../../domain/error';
import { LoadingState } from '../../domain/schemas';
import { UnitDataListDataViewMode } from '../../domain/unitDataList/ranking';

import { SelectOption } from '../../components/molecules/SingleSelect';
import { ConvertRowsToFilteredRows } from '../../components/organisms/TableHeaderSubMenu/ConvertRowsToFilteredRows';

import { RootState } from '../../store';
import { filterModelName } from '../../utils/filterModelName';
import {
  findCellIndexByColumnCode,
  getOrderedTableData,
} from '../../utils/orderedCell';
import { isRowInSelectedHalls } from '../../utils/unitDataListTransitionHalls';
import {
  unitDataListReportsDataViewModeSelector,
  unitDataListReportsOrderSelector,
  unitDataListReportsSortSelector,
  unitDataListReportsTableHeaderSubMenuMultiFilterSelector,
} from '../ui/unitDataListReportsSetting';

// Action Types
const FETCH_DATA_DAI = 'FETCH_DATA_DAI' as const;
const FETCH_DATA_DAI_REQUEST = 'FETCH_DATA_DAI_REQUEST' as const;
const FETCH_DATA_DAI_SUCCESS = 'FETCH_DATA_DAI_SUCCESS' as const;
const RENEW_DATA_DAI = 'RENEW_DATA_DAI' as const;
const SELECT_DATA_DAI_COLUMNS_ORDER = 'SELECT_DATA_DAI_COLUMNS_ORDER' as const;
const CHANGE_DATA_DAI_COLUMNS_ORDER = 'CHANGE_DATA_DAI_COLUMNS_ORDER' as const;
const RESET_DATA_DAI_COLUMNS_ORDER = 'RESET_DATA_DAI_COLUMNS_ORDER' as const;
const SNAPSHOT_DATA_DAI_COLUMNS_ORDER_FOR_DATA_VIEW_MODE_SWITCH =
  'SNAPSHOT_DATA_DAI_COLUMNS_ORDER_FOR_DATA_VIEW_MODE_SWITCH' as const;
const RESET_DATA_DAI_COLUMNS_ORDER_SNAPSHOTS =
  'RESET_DATA_DAI_COLUMNS_ORDER_SNAPSHOTS' as const;
const FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_REQUEST =
  'FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_REQUEST' as const;
const FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_SUCCESS =
  'FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_SUCCESS' as const;
const RESET_DATA_DAI_HIGHLIGHT_AVERAGE =
  'RESET_DATA_DAI_HIGHLIGHT_AVERAGE' as const;

export const DataDaiActionTypes = {
  FETCH_DATA_DAI,
  FETCH_DATA_DAI_REQUEST,
  FETCH_DATA_DAI_SUCCESS,
  RENEW_DATA_DAI,
  SELECT_DATA_DAI_COLUMNS_ORDER,
  CHANGE_DATA_DAI_COLUMNS_ORDER,
  RESET_DATA_DAI_COLUMNS_ORDER,
  SNAPSHOT_DATA_DAI_COLUMNS_ORDER_FOR_DATA_VIEW_MODE_SWITCH,
  RESET_DATA_DAI_COLUMNS_ORDER_SNAPSHOTS,
  FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_REQUEST,
  FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_SUCCESS,
  RESET_DATA_DAI_HIGHLIGHT_AVERAGE,
};

// Action Creators

function fetchDataDaiAction(params: DataDai['setting']) {
  return {
    type: FETCH_DATA_DAI,
    payload: { params },
  };
}

function fetchDataDaiRequestAction() {
  return {
    type: FETCH_DATA_DAI_REQUEST,
  };
}

function fetchDataDaiSuccessAction(dataDai: DataDai) {
  return {
    type: FETCH_DATA_DAI_SUCCESS,
    payload: { dataDai },
  };
}

function renewDataDaiAction() {
  return {
    type: RENEW_DATA_DAI,
  };
}

/**
 *
 * 台別データ一覧の並び替え情報を登録する（Sagaで並び替え確定時）
 */
function selectDataDaiColumnsOrderAction(columnsOrder: string[]) {
  return {
    type: SELECT_DATA_DAI_COLUMNS_ORDER,
    payload: { columnsOrder },
  };
}

/**
 * 台別データ一覧の列の並び替え実行時にSagaで再計算する
 */
function changeDataDaiColumnsOrderAction(
  draggedId?: string,
  droppedId?: string
) {
  return {
    type: CHANGE_DATA_DAI_COLUMNS_ORDER,
    payload: { draggedId, droppedId },
  };
}

/**
 * 台別データ一覧の並び替え情報をリセットする
 */
function resetDataDaiColumnsOrderAction() {
  return {
    type: RESET_DATA_DAI_COLUMNS_ORDER,
  };
}

/**
 * 表示区分（標準/出玉ランキング）切替時に列並び順を退避・復元する
 *
 * 標準／出玉ランキングは表示項目セットが異なり列並び順を共有できないため、
 * 切替元の並び順をモードごとに退避し、切替先で前回退避した並び順（無ければ空＝既定）を復元する
 * @param nextDataViewMode 切替後の表示区分
 * @param outgoingColumnsOrder 切替前（現在）の列並び順
 */
function snapshotDataDaiColumnsOrderForDataViewModeSwitchAction(
  nextDataViewMode: UnitDataListDataViewMode,
  outgoingColumnsOrder: string[]
) {
  return {
    type: SNAPSHOT_DATA_DAI_COLUMNS_ORDER_FOR_DATA_VIEW_MODE_SWITCH,
    payload: { nextDataViewMode, outgoingColumnsOrder },
  };
}

/**
 * 標準／出玉ランキング双方の列並び順の退避内容を破棄する
 *
 * 「リセット」操作は表示区分をまたいだ全ての記憶を消すのが期待される挙動のため、
 * 現在表示中の並び順（columnsOrder）だけでなく、他モードの退避分もあわせて消す。
 * （resetDataDaiColumnsOrderAction は現在の並び順のみを初期化するため、
 * このアクションと組み合わせて使用する）
 */
function resetDataDaiColumnsOrderSnapshotsAction() {
  return {
    type: RESET_DATA_DAI_COLUMNS_ORDER_SNAPSHOTS,
  };
}

/**
 * ハイライト機種のリクエスト
 * @param searchResultSetting 検索条件
 * @param kiList ハイライト機種
 * @param kiName ハイライト機種名（バックエンドからのレスポンスに機種名がないが、テーブルの表示には必要なので引数で受け取って登録している。ここでは正しい動きとなるが、基本的にはfetchにリクエストに必要なパラメータ以外を入れるのはよろしくないので真似しないように）
 */
function fetchDataDaiHighlightAverageRequestAction(
  searchResultSetting: DataDai['setting'],
  kiList: string[] = [],
  kiName = '',
  halls?: string[]
) {
  const hallsForRequest =
    halls && halls.length > 0 ? halls : searchResultSetting.halls;

  return {
    type: FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_REQUEST,
    payload: {
      params: {
        ...searchResultSetting,
        kiList: kiList,
        halls: hallsForRequest,
      },
      kiName: kiName,
    },
  };
}

/**
 * ハイライト機種のリクエスト成功時のアクション
 * @param data APIレスポンス
 */
function fetchDataDaiHighlightAverageSuccessAction(data: DataDai) {
  return {
    type: FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_SUCCESS,
    payload: { data },
  };
}

/**
 * ハイライト機種をリセットする
 */
function resetDataDaiHighlightAverageAction() {
  return {
    type: RESET_DATA_DAI_HIGHLIGHT_AVERAGE,
  };
}

export const DataDaiActionCreators = {
  fetchDataDaiAction,
  fetchDataDaiRequestAction,
  fetchDataDaiSuccessAction,
  renewDataDaiAction,
  selectDataDaiColumnsOrderAction,
  changeDataDaiColumnsOrderAction,
  resetDataDaiColumnsOrderAction,
  snapshotDataDaiColumnsOrderForDataViewModeSwitchAction,
  resetDataDaiColumnsOrderSnapshotsAction,
  fetchDataDaiHighlightAverageRequestAction,
  fetchDataDaiHighlightAverageSuccessAction,
  resetDataDaiHighlightAverageAction,
};

// Actions

export type FetchDataDaiAction = ReturnType<typeof fetchDataDaiAction>;
export type ChangeDataDaiColumnsOrderAction = ReturnType<
  typeof changeDataDaiColumnsOrderAction
>;
type DataDaiAction =
  | FetchDataDaiAction
  | ReturnType<typeof fetchDataDaiRequestAction>
  | ReturnType<typeof fetchDataDaiSuccessAction>
  | ReturnType<typeof renewDataDaiAction>
  | ReturnType<typeof selectDataDaiColumnsOrderAction>
  | ReturnType<typeof resetDataDaiColumnsOrderAction>
  | ReturnType<typeof snapshotDataDaiColumnsOrderForDataViewModeSwitchAction>
  | ReturnType<typeof resetDataDaiColumnsOrderSnapshotsAction>
  | ChangeDataDaiColumnsOrderAction
  | ReturnType<typeof fetchDataDaiHighlightAverageRequestAction>
  | ReturnType<typeof fetchDataDaiHighlightAverageSuccessAction>
  | ReturnType<typeof resetDataDaiHighlightAverageAction>;

// State

type DataDaiState = {
  loadingState: LoadingState;
  dataDai: DataDai;
  columnsOrder?: string[];
  /**
   * 表示区分（標準/出玉ランキング）切替時の列並び順の退避先
   * （標準／出玉ランキングは表示項目セットが異なるため列並びを共有しない）
   */
  standardColumnsOrderSnapshot?: string[];
  rankingColumnsOrderSnapshot?: string[];
  isInitialLoad: boolean;
  /**
   * ハイライト機種の平均合計行データ
   */
  highlightAverage: {
    loadingState: LoadingState;
    error: ClarisApiError | null;
    data: DataDai | null;
    kiList: string[];
    kiName: string;
    halls: string[];
  };
};

const initialState: DataDaiState = {
  loadingState: 'prepare',
  dataDai: dataDaiDefaultValue(),
  columnsOrder: [],
  standardColumnsOrderSnapshot: undefined,
  rankingColumnsOrderSnapshot: undefined,
  isInitialLoad: true,
  highlightAverage: {
    loadingState: 'prepare',
    error: null,
    data: null,
    kiList: [],
    kiName: '',
    halls: [],
  },
};

/**
 * 台別データ一覧 レスポンスデータを取得する（フィルター・ソートされていない）
 * @returns データ
 */
const dataDaiRawSelector = (state: RootState) => state.dataDai.dataDai;

/**
 * [台別データ一覧] ソート処理によって整形されたデータ (ソートラベル・サブメニュー時)
 */
const dataDaiSortedSelector = createSelector(
  [
    dataDaiRawSelector,
    unitDataListReportsSortSelector,
    unitDataListReportsOrderSelector,
  ],
  (dataDai, sort, order) => {
    if (sort == null || order == null) {
      return dataDai;
    }
    return sortDai(dataDai, order, sort);
  }
);

/**
 * 台別データ一覧 データを取得する
 * @returns データ
 */
export const dataDaiSelector = createSelector(
  [
    dataDaiSortedSelector,
    (state: RootState) => state.unitDataListReportsSetting.modelNameFilter,
    (state: RootState) => state.unitDataListReportsSetting.subMenuNumberFilter,
    unitDataListReportsTableHeaderSubMenuMultiFilterSelector,
    unitDataListReportsDataViewModeSelector,
  ],
  (
    sortedDataDai,
    modelNameFilter,
    subMenuNumberFilter,
    tableHeaderSubMenuMultiFilter,
    dataViewMode
  ) => {
    const { columns, rows } = sortedDataDai.data;

    // タイプ列の複数選択は標準表示だけ。出玉ランキングにはメニューを出さない
    const rowsFilteredByType =
      dataViewMode === 'ranking'
        ? rows
        : dataDaiRowsFilteredByMultiFilter(
            columns,
            rows,
            tableHeaderSubMenuMultiFilter
          );

    const { filteredRows } = ConvertRowsToFilteredRows(
      subMenuNumberFilter,
      columns,
      rowsFilteredByType
    );

    //サブメニュー内の数値でフィルタ
    if (!modelNameFilter) {
      return {
        ...sortedDataDai,
        data: {
          ...sortedDataDai.data,
          rows: filteredRows,
        },
      };
    }

    // 機種名でフィルタ
    const kiNameCellIndex = findCellIndexByColumnCode(columns, 'kiTushoMei');
    const modelNameCellIndex = kiNameCellIndex >= 0 ? kiNameCellIndex : 0;

    const predicate = filterModelName(modelNameFilter);
    return {
      ...sortedDataDai,
      data: {
        ...sortedDataDai.data,
        rows: filteredRows.filter(
          (row) =>
            row.data.length > 0 &&
            predicate(row.data.at(modelNameCellIndex)?.value ?? '')
        ),
      },
    };
  }
);

/**
 * 台別データ一覧 レスポンスデータの data 部分を取得する（フィルター・ソートされていない）
 */
const dataDaiAllDataSelector = (state: RootState) => {
  return state.dataDai.dataDai.data;
};

/**
 * 台別データ一覧のテーブルデータのデータ部分のみ取得
 * @returns テーブルデータ（検索条件除いた部分）
 */
const dataDaiDataSelector = createSelector(dataDaiSelector, (dataDai) => {
  return dataDai.data;
});

/**
 * 台別データ一覧のテーブルデータのカラムのみ取得
 * @returns テーブルデータのカラム
 */
export const dataDaiDataColumnsSelector = createSelector(
  dataDaiDataSelector,
  (data) => {
    return data.columns;
  }
);

/**
 * 台別データのデータ部分（データ行）のみ取得
 * @returns テーブルデータのデータ行（columnsを除いた部分）
 */
const dataDaiDataRowSelector = createSelector(dataDaiDataSelector, (data) => {
  return data.rows;
});

/**
 * 取得したテーブルデータを元に選択可能な機種の一覧を取得
 * @returns 選択可能な機種一覧
 */
export const dataDaiSelectOptionsSelector = createSelector(
  [dataDaiDataRowSelector, dataDaiDataColumnsSelector],
  (rows, columns): SelectOption[] => {
    const kiNameCellIndex = findCellIndexByColumnCode(columns, 'kiTushoMei');
    const nameCellIndex = kiNameCellIndex >= 0 ? kiNameCellIndex : 0;

    // 平均合計行を取り除く
    const filteredRows = rows.filter(
      (row) => row.data.at(nameCellIndex)?.value !== '平均/合計'
    );

    // SelectOptionの形に整形する
    return filteredRows.map((row) => ({
      name: row.data.at(nameCellIndex)?.value ?? '',
      value: row.queryParameter.kiList[0],
      type: 'kiList',
    }));
  }
);

/**
 * 台別データ一覧のテーブルの並び替え情報を取得
 * @returns 並び替え情報
 */
export const dataDaiColumnsOrderSelector = (state: RootState) =>
  state.dataDai.columnsOrder;

/**
 * 台別データ一覧のテーブルの並び替え後のテーブルデータを取得する
 * @returns 並び替え後のテーブルデータ
 */
export const dataDaiOrderedDataSelector = createSelector(
  [dataDaiDataSelector, dataDaiColumnsOrderSelector],
  (tableData, ordered) => {
    // テーブルが空の場合はそのまま戻す
    if (tableData === undefined) {
      return { columns: [], rows: [] };
    }

    // 並べ替え後の項目数と、テーブルデータの項目数が一致しない場合、データ取得中
    if (ordered && tableData.columns.length !== ordered.length) {
      return { columns: [], rows: [] };
    }

    const rows = tableData.rows;

    if (!ordered) {
      return { columns: tableData.columns, rows };
    } else {
      return getOrderedTableData(ordered, tableData.columns, rows);
    }
  }
);

/**
 * 台別データ一覧 ローディング状態を取得する
 * @returns ローディング状態
 */
export const dataDaiLoadingStateSelector = (state: RootState) =>
  state.dataDai.loadingState;

/**
 * [台別データ一覧] 初回ロード状態を取得する
 */
export const dataDaiIsInitialLoadSelector = (state: RootState) =>
  state.dataDai.isInitialLoad;

/**
 * 台別データの現在の検索条件
 * @returns 検索条件
 */
export const dataDaiParamsSelector = (state: RootState) =>
  state.dataDai.dataDai?.setting;

/**
 * 現在選択中の表示項目を取得する
 * @returns 選択中の表示項目
 */
export const dataDaiParamsFieldsSelector = createSelector(
  [dataDaiParamsSelector],
  (params) => {
    const selectedFields = params?.fields;

    return selectedFields ? selectedFields : [];
  }
);

/**
 * ハイライト機種の情報を取得
 */
export const dataDaiHighlightAverageSelector = (state: RootState) => {
  return state.dataDai.highlightAverage;
};

/**
 * ハイライトで選択された機種を取得
 */
export const dataDaiHighlightAverageKiListSelector = createSelector(
  dataDaiHighlightAverageSelector,
  (highlightAverage) => highlightAverage.kiList
);

/**
 * ハイライトで選択された店舗を取得
 */
export const dataDaiHighlightAverageHallsSelector = createSelector(
  dataDaiHighlightAverageSelector,
  (highlightAverage) => highlightAverage.halls
);

/**
 * ハイライト機種の平均合計のローディング状態を取得
 */
export const dataDaiHighlightAverageLoadingSelector = createSelector(
  dataDaiHighlightAverageSelector,
  (highlightAverage) => highlightAverage.loadingState
);

/**
 * ハイライトで選択した機種の平均合計行のデータを取得
 */
export const dataDaiHighlightAverageDataSelector = createSelector(
  [
    dataDaiHighlightAverageSelector,
    dataDaiColumnsOrderSelector,
    dataDaiAllDataSelector,
  ],
  (highlightAverage, ordered, dataDai) => {
    if (
      !highlightAverage.data ||
      !highlightAverage.data.data.rows[0] ||
      !highlightAverage.data.setting.kiList
    ) {
      return null;
    }

    const { columns, rows } = highlightAverage.data.data;

    // 並べ替え後の項目数と、テーブルデータの項目数が一致しない場合nullを返す
    if (ordered && columns.length !== ordered.length) {
      return null;
    }

    // 配列DataDaiの内、highlightAverage.kiList・halls に一致する行のみを抽出する
    const kiList = highlightAverage.kiList;
    const halls = highlightAverage.halls;
    const filteredRows = dataDai.rows.filter((row) => {
      const kiMatch = kiList.includes(row.queryParameter.kiList[0]);
      if (!kiMatch) {
        return false;
      }
      return isRowInSelectedHalls(row.queryParameter.halls, halls);
    });

    const firstRow = rows.at(0);

    if (!firstRow) {
      return {
        setting: highlightAverage.data.setting,
        data: { columns, rows: [] },
      };
    }

    // 一列目（機種名列）の文字列を加工する
    // NOTE: 出玉ランキングは機種強調ハイライト・平均合計行を持たないためここは標準表示専用。
    const updatedRows = [
      {
        ...rows[0],
        data: [
          {
            ...firstRow.data[0],
            value: `${highlightAverage.kiName} 平均/合計(${filteredRows.length}台)`,
          },
          ...firstRow.data.slice(1),
        ],
      },
    ];

    if (!ordered) {
      return {
        setting: highlightAverage.data.setting,
        data: { columns, rows: updatedRows },
      };
    } else {
      return {
        setting: highlightAverage.data.setting,
        data: getOrderedTableData(ordered, columns, updatedRows),
      };
    }
  }
);

// Reducer

export function dataDaiReducer(
  state = initialState,
  action: DataDaiAction
): DataDaiState {
  switch (action.type) {
    case FETCH_DATA_DAI_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_DATA_DAI_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
        dataDai: action.payload.dataDai,
        isInitialLoad: false,
      };
    case RENEW_DATA_DAI: {
      return initialState;
    }
    case SELECT_DATA_DAI_COLUMNS_ORDER:
      return {
        ...state,
        columnsOrder: action.payload.columnsOrder,
      };
    case RESET_DATA_DAI_COLUMNS_ORDER:
      return {
        ...state,
        columnsOrder: initialState.columnsOrder,
      };
    case RESET_DATA_DAI_COLUMNS_ORDER_SNAPSHOTS:
      // 標準／出玉ランキング双方の退避内容を破棄する（「リセット」操作専用）
      return {
        ...state,
        standardColumnsOrderSnapshot: initialState.standardColumnsOrderSnapshot,
        rankingColumnsOrderSnapshot: initialState.rankingColumnsOrderSnapshot,
      };
    case SNAPSHOT_DATA_DAI_COLUMNS_ORDER_FOR_DATA_VIEW_MODE_SWITCH: {
      const { nextDataViewMode, outgoingColumnsOrder } = action.payload;
      if (nextDataViewMode === 'ranking') {
        return {
          ...state,
          standardColumnsOrderSnapshot: outgoingColumnsOrder,
          columnsOrder: state.rankingColumnsOrderSnapshot ?? [],
        };
      }
      return {
        ...state,
        rankingColumnsOrderSnapshot: outgoingColumnsOrder,
        columnsOrder: state.standardColumnsOrderSnapshot ?? [],
      };
    }
    case FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_REQUEST: {
      return {
        ...state,
        highlightAverage: {
          loadingState: 'loading',
          error: null,
          data: null,
          kiList: action.payload.params.kiList,
          kiName: action.payload.kiName,
          halls: action.payload.params.halls ?? [],
        },
      };
    }
    case FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_SUCCESS: {
      return {
        ...state,
        highlightAverage: {
          ...state.highlightAverage,
          loadingState: 'loaded',
          error: null,
          data: action.payload.data,
        },
      };
    }
    case RESET_DATA_DAI_HIGHLIGHT_AVERAGE: {
      return {
        ...state,
        highlightAverage: initialState.highlightAverage,
      };
    }
    default:
      return state;
  }
}
